import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Paper,
  Modal,
  RadioGroup,
  Radio,
  Button
} from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';


const permissionGroups = {
  Admin_Reports: [
    'view_active_customer_report',
    'view_client_status_report',
    'view_client_termination_report',
    'view_employee_notes_report',
    'view_plan_renewal_report',
    'view_tasks_report'
  ],
  BI_Dashboards: [
    'access_customer_dashboard',
    'access_demographics_dashboard',
    'access_employee_dashboard',
    'access_financial_dashboard',
    'access_hr_dashboard',
    'access_operations_dashboard'
  ],
  Billing_Reports: [
    'view_billing_outstanding_balance_report',
    'view_billing_transaction_report',
    'view_claim_balance_report',
    'view_claim_collection_report',
    'view_prior_auth_utilization_report',
    'view_service_authorization_report'
  ],
  Billing_Tab: [
    'access_accounts_receivable_tab',
    'access_change_capture_tab',
    'access_claims_tab',
    'access_prior_authorization_tab',
    'access_remittances_tab'
  ],
  Case_Management_Reports: [
    'view_case_management_report'
  ],
  Company_Reports: [
    'view_debarment_report',
    'view_nj_consolidated_debarment_report',
    'view_payroll_report'
  ],
  EVV_Reports: [
    'view_evv_invoice',
    'view_evv_report'
  ],
  Forms_Tab: [
    'access_form_builder_tab',
    'access_form_library_tab',
    'access_form_reports_tab',
    'access_report_builder_tab'
  ],
  Mobile_App_Screens: [
    'access_Artifacts_screen',
    'access_Checklist_screen',
    'access_Contacts_screen',
    'access_Customers_screen',
    'access_Home_screen',
    'access_Notes_screen',
    'access_Projects_screen',
    'access_Record_Activity_screen',
    'access_Tasks_screen',
    'access_Timesheets_screen',
  ],
  Resource_Functionalities: [
    'create_new_project',
    'access_admin_level_projects',
    'access_manager_level_projects',
    'access_employee_level_projects',
    'create_new_customer',
    'access_admin_level_customers',
    'access_manager_level_customers',
    'invite_employees',
    'create_intake'
  ],
  Scheduler_Functionalities: [
    'schedule_session',
    'schedule_session_for_other_employees',
    'edit_session',
    'mark_attendance',
    'add_session_note',
    'modify_session_task'
  ],
  Scheduler_Reports: [
    'view_client_attendance_report',
    'view_daily_attendance_summary',
    'view_employee_sessions_report'
  ],
  Settings_Tabs: [
    'access_settings_advanced_tab',
    'access_settings_agency_tab',
    'access_settings_billing_tab',
    'access_settings_employee_tab',
    'access_settings_general_tab',
    'access_settings_subscription_tab',
    'access_settings_user_experience_tab'
  ],
  Support_Coordination_Reports: [
    'view_late_mt_report',
    'view_mt_completion_detail_report',
    'view_mt_rejection_report',
    'view_mt_summary_report',
    'view_management_review_report',
    'view_visit_report'
  ],
  Workflows_Tabs: [
    'access_active_workflows_tab',
    'access_workflow_builder_tab',
    'access_workflow_dashboard_tab',
    'access_workflow_library_tab'
  ]
};

const permissionsMap = {
  // Admin Reports
  view_active_customer_report: 'ActiveCustomerReport',
  view_client_status_report: 'ClientStatusReport',
  view_client_termination_report: 'ClientTerminationReport',
  view_employee_notes_report: 'EmployeeNotesReport',
  view_plan_renewal_report: 'PlanRenewalReport',
  view_tasks_report: 'TasksReport',

  // BI Dashboards
  access_customer_dashboard: 'CustomerDashboard',
  access_demographics_dashboard: 'DemographicsDashboard',
  access_employee_dashboard: 'EmployeeDashboard',
  access_financial_dashboard: 'FinancialDashboard',
  access_hr_dashboard: 'HRDashboard',
  access_operations_dashboard: 'OperationsDashboard',

  // Billing Reports
  view_billing_outstanding_balance_report: 'BillingOutstandingBalanceReport',
  view_billing_transaction_report: 'BillingTransactionReport',
  view_claim_balance_report: 'ClaimBalanceReport',
  view_claim_collection_report: 'ClaimCollectionReport',
  view_prior_auth_utilization_report: 'PriorAuthUtilizationReport',
  view_service_authorization_report: 'ServiceAuthorizationReport',

  // Billing Tabs
  access_accounts_receivable_tab: 'AccountsReceivableTab',
  access_change_capture_tab: 'ChangeCaptureTab',
  access_claims_tab: 'ClaimsTab',
  access_prior_authorization_tab: 'PriorAuthorizationTab',
  access_remittances_tab: 'RemittancesTab',

  // Case Management Reports
  view_case_management_report: 'CaseManagementReport',

  // Company Reports
  view_debarment_report: 'DebarmentReport',
  view_nj_consolidated_debarment_report: 'NJConsolidatedDebarmentReport',
  view_payroll_report: 'PayrollReport',

  // EVV Reports
  view_evv_invoice: 'EVVInvoice',
  view_evv_report: 'EVVReport',

  // Forms Tabs
  access_form_builder_tab: 'FormBuilderTab',
  access_form_library_tab: 'FormLibraryTab',
  access_form_reports_tab: 'FormReportsTab',
  access_report_builder_tab: 'ReportBuilderTab',

  // Resource Functionalities
  create_new_project: 'CreateNewProject',
  access_admin_level_projects: 'AdminLevelProjects',
  access_manager_level_projects: 'ManagerLevelProjects',
  access_employee_level_projects: 'EmployeeLevelProjects',
  create_new_customer: 'CreateNewCustomer',
  access_admin_level_customers: 'AdminLevelCustomers',
  access_manager_level_customers: 'ManagerLevelCustomers',
  invite_employees: 'InviteEmployees',
  create_intake: 'CreateIntake',

  // Scheduler Functionalities
  schedule_session: 'ScheduleSession',
  schedule_session_for_other_employees: 'ScheduleSessionForOtherEmployees',
  edit_session: 'EditSession',
  mark_attendance: 'MarkAttendance',
  add_session_note: 'AddSessionNote',
  modify_session_task: 'ModifySessionTask',

  // Scheduler Reports
  view_client_attendance_report: 'ClientAttendanceReport',
  view_daily_attendance_summary: 'DailyAttendanceSummary',
  view_employee_sessions_report: 'EmployeeSessionsReport',

  // Settings Tabs
  access_settings_advanced_tab: 'SettingsAdvancedTab',
  access_settings_agency_tab: 'SettingsAgencyTab',
  access_settings_billing_tab: 'SettingsBillingTab',
  access_settings_employee_tab: 'SettingsEmployeeTab',
  access_settings_general_tab: 'SettingsGeneralTab',
  access_settings_subscription_tab: 'SettingsSubscriptionTab',
  access_settings_user_experience_tab: 'SettingsUserExperienceTab',

  // Support Coordination Reports
  view_late_mt_report: 'LateMTReport',
  view_mt_completion_detail_report: 'MTCompletionDetailReport',
  view_mt_rejection_report: 'MTRejectionReport',
  view_mt_summary_report: 'MTSummaryReport',
  view_management_review_report: 'ManagementReviewReport',
  view_visit_report: 'VisitReport',

  // Workflows Tabs
  access_active_workflows_tab: 'ActiveWorkflowsTab',
  access_workflow_builder_tab: 'WorkflowBuilderTab',
  access_workflow_dashboard_tab: 'WorkflowDashboardTab',
  access_workflow_library_tab: 'WorkflowLibraryTab',

  // Mobile App Screen Tabs
  access_Artifacts_screen: 'ArtifactsScreenAccess',
  access_Checklist_screen: 'ChecklistScreenAccess',
  access_Contacts_screen: 'ContactsScreenAccess',
  access_Customers_screen: 'CustomersScreenAccess',
  access_Home_screen: 'HomeScreenAccess',
  access_Notes_screen: 'NotesScreenAccess',
  access_Projects_screen: 'ProjectsScreenAccess',
  access_Record_Activity_screen: 'RecordActivityScreenAccess',
  access_Tasks_screen: 'TasksScreenAccess',
  access_Timesheets_screen: 'TimesheetsScreenAccess',
};


const Permissions = (props) => {
  const [managerRoleQuestion, setManagerRoleQuestion] = useState(true);
  const [newManagerRole, setNewManagerRole] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const allGroups = Object.entries(permissionGroups).filter(([group, perms]) =>
    perms.some((perm) => props.refPermissions[permissionsMap[perm]])
  );

  // Sort groups by size (optional: could sort descending to help balance early)
  const sortedGroups = allGroups.sort((a, b) => a[1].length - b[1].length);

  let leftColumn = [];
  let rightColumn = [];
  let leftCount = 0;
  let rightCount = 0;

  for (const [group, perms] of sortedGroups) {
    if (leftCount <= rightCount) {
      leftColumn.push([group, perms]);
      leftCount += perms.length;
    } else {
      rightColumn.push([group, perms]);
      rightCount += perms.length;
    }
  }


  useEffect(() => {
    setManagerRoleQuestion(!!props.addNew);
  }, [props.addNew]);

  useEffect(() => {
    setPermissions(props.empType.permissions);
  }, [props]);

  useEffect(() => {
    if (!permissions) {

    }
  }, [])

  const handleManagerAnswer = (answer) => {
    if (answer === 'yes') {
      setNewManagerRole(true);
      props.handleNewEmpTypeUpdate("manager");
    } else {
      props.handleNewEmpTypeUpdate("employee");
    }
    setManagerRoleQuestion(false);
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    setHasUnsavedChanges(false);
    props.onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };


  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      props.onClose();
    }
  }

  const handlePermissionChange = (key, val) => {
    setHasUnsavedChanges(true);
    props.handleUpdatePermissions(key, val);
  }

  return (
    <div>
    <Dialog open={showConfirmDialog} onClose={handleCancelClose}>
      <DialogTitle>Unsaved Changes</DialogTitle>
      <DialogContent>
        <Typography>Are you sure you want to close? Changes you made may not be saved.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelClose}>Cancel</Button>
        <Button onClick={handleConfirmClose} color="error">Discard Changes</Button>
      </DialogActions>
    </Dialog>

    <Modal open={props.open} onClose={handleClose}>

      {<Paper sx={{ p: 2, maxWidth: 800, mx: 'auto', my: 8, maxHeight: '70vh' }}>
      
        {managerRoleQuestion ? (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Will this role manage other employees?
            </Typography>
            <RadioGroup
              name="manageEmployees"
              onChange={(e) => handleManagerAnswer(e.target.value)}
              row
            >
              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
            </RadioGroup>
          </>
        ) : (
          <>
            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Set permissions for {props.empType.title}
              </Typography>
              <Box textAlign="right" sx={{ display:'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
                <Button onClick={handleClose} size="small" variant="outlined" sx={{ mr: 1}}>Close</Button>
                <Button onClick={props.onSave} size="small" variant="contained" >Save</Button>
              </Box>
            </div>
            <Box sx={{ display: 'flex', gap: 2, maxHeight: '60vh', overflow: 'auto' }}>
              {[leftColumn, rightColumn].map((column, i) => (
                <Box key={i} sx={{ flex: 1 }}>
                  {column.map(([group, perms]) => (
                    <Box key={group} sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.primary" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                        {group.split("_").join(" ")}
                      </Typography>
                      <FormGroup>
                        {perms
                          .filter((perm) => props.refPermissions[permissionsMap[perm]])
                          .map((perm) => (<FormControlLabel
                              key={perm}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={permissions ? !!permissions[permissionsMap[perm]] : false}
                                  onChange={(event, val) => handlePermissionChange(permissionsMap[perm],val)}
                                  disabled={!props.editMode}
                                />
                              }
                              label={<Typography variant="body2">{perm.split("_").join(" ")}</Typography>}
                              sx={{ ml: 1, my: 0 }}
                            />
                          ))}
                      </FormGroup>
                      <Divider sx={{ mt: 1.5 }} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </>
        )}
      </Paper>}
    </Modal>
    </div>
  );
};

export default Permissions;
